'use client';

import { logger } from '@adornis/base/logging.js';
import { Menu } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTeacherByUserIdQuery } from '../../db/api/get-teacher.js';
import type { ViewState } from '../app/types.js';
import { Chat } from '../components/chat/chat.js';
import { ClassInsightsPage } from '../components/pages/class-insights-page.js';
import { ClassroomPage } from '../components/pages/classroom-page.js';
import { DummyView } from '../components/pages/dummy.js';
import { FeedbackPage } from '../components/pages/feedback-page.js';
import { JoinClassPage } from '../components/pages/join-class-page.js';
import { ProfilePage } from '../components/pages/profile-page.js';
import { SettingsPage } from '../components/pages/settings-page.js';
import { ExamNotificationToaster } from '../components/pop-up/exam-notification-toaster.js';
import { Sidebar } from '../components/sidebar/sidebar.js';
import { consumePendingJoinCode } from '../lib/pending-join-code.js';
import { useCurrentUser } from '../lib/useCurrentUser.js';

type HomeProps = {
  onNavigate?: (view: ViewState, title?: string) => void;
  onOpenLogin?: () => void;
  onOpenSignup?: () => void;
  onGuestMessageSent?: (conversationId: string) => void;
};

export default function Home({ onNavigate, onOpenLogin, onOpenSignup, onGuestMessageSent }: HomeProps) {
  const { user } = useCurrentUser();
  const { i18n } = useTranslation();
  const [currentView, setCurrentView] = useState<ViewState>('chat');
  const [sidebarTitle, setSidebarTitle] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [conversationId, setConversationId] = useState<string>('new');
  const [chatNavigationVersion, setChatNavigationVersion] = useState(0);
  const [pendingJoinCode, setPendingJoinCode] = useState<string | null>(null);
  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  const isGuest = !user;

  const handleNavigate = (view: ViewState, title?: string) => {
    setCurrentView(view);
    if (view === 'chat') {
      setChatNavigationVersion(prev => prev + 1);
      if (title) {
        setConversationId(title);
      }
    }
    setSidebarTitle(title ?? '');
  };

  // Wenn sich der angemeldete Benutzer ändert (Login / Logout), laden wir die Chat-Komponenten neu.
  // `currentUser()` kann während eines Logins mehrfach emittieren – wir reagieren daher nur auf einen
  // echten Identitätswechsel (per Ref), sonst würde eine spätere Emission die Navigation zurücksetzen.
  // Bei Login navigieren wir zur Konversation, in der der Gast gerade war (von auth-modal.tsx via
  // `claimedConversationId` übergeben), statt zum Dashboard ('new').
  useEffect(() => {
    const currentUserId = (user as any)?._id ?? null;
    if (prevUserIdRef.current === currentUserId) return;
    prevUserIdRef.current = currentUserId;

    let restoreId: string | null = null;
    if (currentUserId) {
      try {
        restoreId = localStorage.getItem('claimedConversationId');
        if (restoreId) localStorage.removeItem('claimedConversationId');
      } catch (e) {}
    }
    setConversationId(restoreId || 'new');
    setChatNavigationVersion(prev => prev + 1);

    // A join code stashed by the `/join?code=` landing page (see client/app/join/page.tsx) survives
    // the login/signup round-trip in localStorage; once we have a real user, consume it and jump
    // straight into the join flow instead of landing on the dashboard.
    if (currentUserId) {
      const pendingCode = consumePendingJoinCode();
      if (pendingCode) {
        setPendingJoinCode(pendingCode);
        handleNavigate('joinClass');
      }
    }
  }, [user]);

  // Gäste haben keinen Zugriff auf die Profilseite (z. B. bei Logout während des Betrachtens).
  useEffect(() => {
    if (isGuest && (currentView === 'profile' || currentView === 'classInsights' || currentView === 'classroom' || currentView === 'joinClass')) {
      handleNavigate('chat');
    }
  }, [isGuest, currentView]);

  useEffect(() => {
    const userId = (user as any)?._id;
    if (!userId) return;

    let isActive = true;

    void getTeacherByUserIdQuery(userId)({ language: 1 })
      .then(teachers => {
        if (!isActive) return;
        const preferredLanguage = String(teachers?.[0]?.language || '').trim();
        if (preferredLanguage && preferredLanguage !== i18n.language) {
          void i18n.changeLanguage(preferredLanguage).catch(err => {
            logger.error(err, 'Failed to apply teacher language preference');
          });
        }
      })
      .catch(err => {
        logger.error(err, 'Failed to load teacher language preference');
      });

    return () => {
      isActive = false;
    };
  }, [user, i18n]);

  return (
    <main className="flex h-screen overflow-hidden bg-background">
      <Sidebar
        onNavigate={handleNavigate}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onCollapsedChange={setIsSidebarCollapsed}
        onOpenLogin={onOpenLogin}
        onOpenSignup={onOpenSignup}
        activeConversationId={conversationId}
      />

      {!isMobileSidebarOpen && (
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="fixed top-4 left-4 z-30 md:hidden bg-brand-strong text-white p-3 rounded-lg shadow-lg hover:bg-brand-700 transition-colors"
        >
          <Menu className="h-6 w-6" />
        </button>
      )}

      {/* z-0 pins its own stacking context so nothing inside — including body-portaled floating
          panels like the exam editor's popovers — can ever paint over the sidebar/app chrome,
          no matter what z-index that content uses internally. */}
      <section className="flex-1 flex flex-col h-full relative z-0 overflow-hidden">
        {currentView === 'chat' ? (
          <Chat
            conversationId={conversationId}
            navigationVersion={chatNavigationVersion}
            onMessageSent={(id) => {
              setIsSidebarCollapsed(true);
              onGuestMessageSent?.(id);
            }}
            onOpenLogin={onOpenLogin}
            onOpenSignup={onOpenSignup}
            onConversationIdChange={setConversationId}
          />
        ) : currentView === 'profile' && !isGuest ? (
          <ProfilePage onBack={() => handleNavigate('chat')} />
        ) : currentView === 'classInsights' && !isGuest ? (
          <ClassInsightsPage
            className={sidebarTitle}
            onBack={() => handleNavigate('profile')}
            onOpenExam={conversationId => handleNavigate('chat', conversationId)}
          />
        ) : currentView === 'classroom' && !isGuest ? (
          <ClassroomPage
            onBack={() => handleNavigate('chat')}
            onOpenExam={conversationId => handleNavigate('chat', conversationId)}
          />
        ) : currentView === 'joinClass' && !isGuest ? (
          <JoinClassPage
            onBack={() => handleNavigate('chat')}
            initialCode={pendingJoinCode}
            onInitialCodeConsumed={() => setPendingJoinCode(null)}
          />
        ) : currentView === 'feedback' ? (
          <FeedbackPage onBack={() => handleNavigate('chat')} />
        ) : currentView === 'settings' ? (
          <SettingsPage onBack={() => handleNavigate('chat')} />
        ) : (
          <DummyView title={sidebarTitle} onBack={() => handleNavigate('chat')} />
        )}
      </section>

      <ExamNotificationToaster
        onOpenExam={conversationId => handleNavigate('chat', conversationId)}
        activeConversationId={conversationId}
      />
    </main>
  );
}
